import { AnalyticsEvent, logAnalyticsEvent } from 'utils/analytics'
import { CORAL_700, GRAY_600, GRAY_700, GRAY_900, MOBILE_VIEW_WIDTH_QUERY } from 'constants/styling/theme'
import { DeleteOutlineOutlined, HelpOutline } from '@mui/icons-material'
import { DropdownInputTypes, MUIDropdownInput } from 'components/common/MUIDropdownInput/MUIDropdownInput.component'
import { FilterType, usePropertyDashboardFilter } from '../_main/context/PropertyDashboardFilter.context'
import { KeyboardEventKey, Params } from 'constants/misc'

import Box from '@mui/material/Box'
import { FC } from 'react'
import { FilterTab } from 'components/common/FilterTab'
import { MUIDropdown } from 'components/common/MUIDropdown'
import { MUIDropdownItem } from 'components/common/MUIDropdownItem'
import { MUIInputField } from 'components/common/MUIInputField'
import { MUITooltip } from 'components/common/MUITooltip'
import { OrderAssignmentStatusFilter } from 'constants/misc'
import { PropertiesDashboardFilterDrawer } from './PropertiesDashboardFilterDrawer.component'
import { PropertiesDateFilter } from './PropertyDateFilter.component'
import SearchIcon from '@mui/icons-material/Search'
import SortIcon from '@mui/icons-material/Sort'
import Stack from '@mui/material/Stack'
import Typography from '@mui/material/Typography'
import { useMediaQuery } from '@mui/material'
import { useTranslation } from 'react-i18next'

/**
 * @component Properties dashboard filter which contains manager dropdown, date range picker, order status filter and search input.
 * 
 * @example
 * <PropertiesDashboardFilter />
 */
export const PropertiesDashboardFilter: FC = () => {

  const { t } = useTranslation(['properties_dashboard'])

  const {
    showManagersInput,
    propertyParams,
    searchProperty,
    managersList,
    openFilter,
    setOpenFilter,
    handleCloseFilter,
    selectedOrderStatus,
    disableSearchManager,
    selectedManager,
    getOrderStatusDisplayText,
    handleOrderStatusChange,
    handleRemoveOrderStatus,
    handleChangeManager,
    handleRemoveManager,
    isFilterIconActive,
    setIsFilterIconActive,
    setIsDrawerOpen
  } = usePropertyDashboardFilter()

  const isMobileView = useMediaQuery(MOBILE_VIEW_WIDTH_QUERY)

  return (
    <Stack flexDirection="row" gap="1.6rem" alignItems="center" marginBottom="1.6rem" justifyContent="space-between">

      {/* Search Input */}
      <Box sx={{ flexGrow: 1 }}>
        <MUIInputField
          value={searchProperty ?? ''}
          placeholder={t('property_search_placeholder')}
          onChange={e => {
            propertyParams.set([Params.SEARCH, e.target.value])
          }}
          onKeyDown={e => {
            if (e.key === KeyboardEventKey.ENTER) { e.preventDefault() } // Prevent form submission on enter
          }}
          startIcon={<SearchIcon sx={{ fontSize: '2rem', color: GRAY_600, paddingLeft: 0 }} />}
          endIcon={
            <MUITooltip content={t('property_search_tooltip')} type="secondary">
              <HelpOutline sx={{ fontSize: '1.8rem', color: GRAY_600, cursor: 'pointer', paddingTop: '0.4rem' }} />
            </MUITooltip>
          }
          inputBoxWidth={isMobileView ? 'unset' : '32rem'}
          inputBoxHeight="3.8rem"
        />
      </Box>

      {isMobileView ?
        <FilterTab
          height="4rem"
          isActive={isFilterIconActive}
          icon={<SortIcon fontSize='inherit' />}
          onClick={() => {
            setIsFilterIconActive(true)
            setIsDrawerOpen(true)
          }}
        />
        :
        <Stack flexDirection="row" gap="1.6rem" alignItems="center">

          {/* Filter by label */}
          <Typography variant="text-sm" fontWeight={600} color={GRAY_700}>
            {t('filter_by')}:
          </Typography>

          {/* Property Creation Date Filter */}
          <PropertiesDateFilter />

          {/* Order Status Filter */}
          <Box sx={{ width: '16rem' }}>
            <MUIDropdown
              open={openFilter === FilterType.ORDER_STATUS}
              onClose={handleCloseFilter}
              button={(isOpen, action, onClose) => (
                <MUIDropdownInput
                  readOnly={true}
                  value={selectedOrderStatus ? getOrderStatusDisplayText(selectedOrderStatus) : ''}
                  placeholder={t('order_status')}
                  type={DropdownInputTypes.SELECTOR}
                  sx={{
                    '& .MuiInputBase-input': {
                      fontSize: '1.6rem',
                      fontWeight: 400,
                    }
                  }}
                  onClick={(e) => {
                    logAnalyticsEvent(AnalyticsEvent.FILTER_PROPERTY, { filterType: FilterType.ORDER_STATUS })
                    setOpenFilter(FilterType.ORDER_STATUS)
                    action(e)
                  }}
                  customInputContent={!!selectedOrderStatus &&
                    <Stack
                      flexDirection="row"
                      paddingRight="0.4rem"
                      fontSize="1.4rem"
                      alignItems="center"
                      paddingTop="0.1rem"
                      color={GRAY_900}
                      onClick={handleRemoveOrderStatus}
                      sx={{ cursor: 'pointer', ':hover': { color: CORAL_700 } }}
                    >
                      <DeleteOutlineOutlined fontSize="inherit" />
                    </Stack>
                  }
                />
              )}
            >
              <Box width="16rem">
                <MUIDropdownItem onClick={() => handleOrderStatusChange('')} sx={{ textWrap: 'auto' }}>
                  <Typography variant="text-sm" color={GRAY_900} fontWeight="medium">
                    {t('all')}
                  </Typography>
                </MUIDropdownItem>
                <MUIDropdownItem onClick={() => handleOrderStatusChange(OrderAssignmentStatusFilter.ONGOING)} sx={{ textWrap: 'auto' }}>
                  <Typography variant="text-sm" color={GRAY_900} fontWeight="medium">
                    {t('ongoing')}
                  </Typography>
                </MUIDropdownItem>
                <MUIDropdownItem onClick={() => handleOrderStatusChange(OrderAssignmentStatusFilter.NEEDS_ATTENTION)} sx={{ textWrap: 'auto' }}>
                  <Typography variant="text-sm" color={GRAY_900} fontWeight="medium">
                    {t('needs_attention')}
                  </Typography>
                </MUIDropdownItem>
                <MUIDropdownItem onClick={() => handleOrderStatusChange(OrderAssignmentStatusFilter.DELIVERED)} sx={{ textWrap: 'auto' }}>
                  <Typography variant="text-sm" color={GRAY_900} fontWeight="medium">
                    {t('delivered')}
                  </Typography>
                </MUIDropdownItem>
                <MUIDropdownItem onClick={() => handleOrderStatusChange(OrderAssignmentStatusFilter.CANCELLED)} sx={{ textWrap: 'auto' }}>
                  <Typography variant="text-sm" color={GRAY_900} fontWeight="medium">
                    {t('cancelled')}
                  </Typography>
                </MUIDropdownItem>
              </Box>
            </MUIDropdown>
          </Box>

          {/* Manager Filter - Only render if managers exist */}
          {showManagersInput && (
            <Box sx={{ width: '20.8rem' }}>
              <MUIDropdown
                open={openFilter === FilterType.MANAGER}
                onClose={handleCloseFilter}
                button={(isOpen, action, onClose) => (
                  <MUIDropdownInput
                    readOnly={disableSearchManager}
                    value={selectedManager ?? ''}
                    onChange={e => handleChangeManager(e.target.value)}
                    placeholder={t('manager')}
                    type={DropdownInputTypes.SELECTOR}
                    sx={{
                      '& .MuiInputBase-input': {
                        fontSize: '1.6rem',
                        fontWeight: 400,
                      }
                    }}
                    onClick={(e) => {
                      logAnalyticsEvent(AnalyticsEvent.FILTER_PROPERTY, { filterType: FilterType.MANAGER })
                      setOpenFilter(FilterType.MANAGER)
                      action(e)
                    }}
                    customInputContent={!!selectedManager &&
                      <Stack
                        flexDirection="row"
                        paddingRight="0.4rem"
                        fontSize="1.4rem"
                        alignItems="center"
                        paddingTop="0.1rem"
                        color={GRAY_900}
                        onClick={handleRemoveManager}
                        sx={{ cursor: 'pointer', ':hover': { color: CORAL_700 } }}
                      >
                        <DeleteOutlineOutlined fontSize="inherit" />
                      </Stack>
                    }
                  />
                )}
              >
                <Box width="20.5rem">
                  {managersList.map(({ id, name }) =>
                    <MUIDropdownItem key={id} onClick={() => handleChangeManager(name, id)} sx={{ textWrap: 'auto' }}>
                      <Typography variant="text-sm" color={GRAY_900} fontWeight="medium">
                        {name}
                      </Typography>
                    </MUIDropdownItem>
                  )}
                </Box>
              </MUIDropdown>
            </Box>
          )}

        </Stack>
      }

      <PropertiesDashboardFilterDrawer />

    </Stack>
  )
}
